import React, { useRef, useEffect, useState } from 'react';
import axios from 'axios';

const NurseryLinesStencilsCanvas = ({ width = 400, height = 400, rows = 5, cols = 5, onClose }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [convertedText, setConvertedText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const drawColor = 'black'; 
  const [zoom, setZoom] = useState(1); 
  
  const zoomIn = () => setZoom(prevZoom => Math.min(prevZoom * 1.2, 5)); 
  const zoomOut = () => setZoom(prevZoom => Math.max(prevZoom / 1.2, 0.2)); 

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Clear canvas before drawing the grid
    ctx.clearRect(0, 0, width, height);

    // Set canvas background color
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, width, height);

    // Draw the grid
    const cellWidth = width / cols;
    const cellHeight = height / rows;

    ctx.strokeStyle = '#ADD8E6'; 
    ctx.lineWidth = 1;
    ctx.setTransform(zoom, 0, 0, zoom, 0, 0); // Apply zoom

    for (let i = 0; i <= cols; i++) {
        const x = i * cellWidth;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
    }

    for (let i = 0; i <= rows; i++) {
        const y = i * cellHeight;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
    }

    // Define drawing functions for different shapes
    const drawSleepingLine = (x, y, w, h) => {
        const offset = 0.25 * Math.min(w, h);
        ctx.beginPath();
        ctx.moveTo(x + offset, y + h / 2);
        ctx.lineTo(x + w - offset, y + h / 2);
        ctx.stroke();
    };

    const drawStandingLine = (x, y, w, h) => {
        const offset = 0.25 * Math.min(w, h);
        ctx.beginPath();
        ctx.moveTo(x + w / 2, y + offset);
        ctx.lineTo(x + w / 2, y + h - offset);
        ctx.stroke();
    };

    const drawLeftSlantingLine = (x, y, w, h) => {
        const offset = 0.25 * Math.min(w, h);
        ctx.beginPath();
        ctx.moveTo(x + offset, y + offset);
        ctx.lineTo(x + w - offset, y + h - offset);
        ctx.stroke();
    };

    const drawRightSlantingLine = (x, y, w, h) => {
        const offset = 0.25 * Math.min(w, h);
        ctx.beginPath();
        ctx.moveTo(x + w - offset, y + offset);
        ctx.lineTo(x + offset, y + h - offset);
        ctx.stroke();
    };

    const drawLeftCurve = (x, y, w, h) => {
        const radius = 0.4 * Math.min(w, h);
        ctx.beginPath();
        ctx.arc(x + w / 2, y + h / 2, radius, Math.PI, 1.5 * Math.PI);
        ctx.stroke();
    };

    const drawRightCurve = (x, y, w, h) => {
        const radius = 0.4 * Math.min(w, h);
        ctx.beginPath();
        ctx.arc(x + w / 2, y + h / 2, radius, 1.5 * Math.PI, 2 * Math.PI);
        ctx.stroke();
    };

    const drawUpperCurve = (x, y, w, h) => {
        const radius = 0.4 * Math.min(w, h);
        ctx.beginPath();
        ctx.arc(x + w / 2, y + h / 2, radius, Math.PI * 1.5, Math.PI * 2);
        ctx.stroke();
    };

    const drawLowerCurve = (x, y, w, h) => {
        const radius = 0.4 * Math.min(w, h);
        ctx.beginPath();
        ctx.arc(x + w / 2, y + h / 2, radius, 0, Math.PI / 2);
        ctx.stroke();
    };

    const drawCircle = (x, y, w, h) => {
        const radius = 0.4 * Math.min(w, h);
        ctx.beginPath();
        ctx.arc(x + w / 2, y + h / 2, radius, 0, 2 * Math.PI);
        ctx.stroke();
    };

    // Draw shapes in each cell
    const shapes = [
        drawSleepingLine,
        drawStandingLine,
        drawLeftSlantingLine,
        drawRightSlantingLine,
        drawLeftCurve,
        drawRightCurve,
        drawUpperCurve,
        drawLowerCurve,
        drawCircle
    ];
    ctx.strokeStyle = 'gray';

    let shapeIndex = 0;
    for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
            const x = col * cellWidth;
            const y = row * cellHeight;
            const w = cellWidth;
            const h = cellHeight;

            // Draw the current shape
            shapes[shapeIndex](x, y, w, h);

            // Move to the next shape
            shapeIndex = (shapeIndex + 1) % shapes.length;
        }
    }
}, [width, height, rows, cols, zoom]);

  

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    const startDrawing = (offsetX, offsetY) => {
      ctx.strokeStyle = drawColor; 
      ctx.lineWidth = 2;  
      ctx.beginPath();
      ctx.moveTo(offsetX, offsetY);
      setIsDrawing(true);
    };

    const draw = (offsetX, offsetY) => {
      if (!isDrawing) return;
      ctx.lineTo(offsetX, offsetY);
      ctx.stroke();
    };

    const endDrawing = () => {
      if (isDrawing) {
        setIsDrawing(false);
        ctx.beginPath(); 
      }
    };

    const handleMouseDown = (e) => {
      const rect = canvas.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const offsetY = e.clientY - rect.top;
      startDrawing(offsetX, offsetY);
    };

    const handleMouseMove = (e) => {
      if (!isDrawing) return;
      const rect = canvas.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const offsetY = e.clientY - rect.top;
      draw(offsetX, offsetY);
    };

    const handleTouchStart = (e) => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const offsetX = e.touches[0].clientX - rect.left;
      const offsetY = e.touches[0].clientY - rect.top;
      startDrawing(offsetX, offsetY);
    };

    const handleTouchMove = (e) => {
      e.preventDefault();
      if (!isDrawing) return;
      const rect = canvas.getBoundingClientRect();
      const offsetX = e.touches[0].clientX - rect.left;
      const offsetY = e.touches[0].clientY - rect.top;
      draw(offsetX, offsetY);
    };

    // const zoomIn = () => setZoom(prevZoom => Math.min(prevZoom * 1.2, 5)); 
    // const zoomOut = () => setZoom(prevZoom => Math.max(prevZoom / 1.2, 0.2)); 

    const handleMouseUp = endDrawing;
    const handleMouseOut = endDrawing;
    const handleTouchEnd = endDrawing;

    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseup', handleMouseUp);
    canvas.addEventListener('mouseout', handleMouseOut);
    canvas.addEventListener('touchstart', handleTouchStart);
    canvas.addEventListener('touchmove', handleTouchMove);
    canvas.addEventListener('touchend', handleTouchEnd);

    return () => {
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseup', handleMouseUp);
      canvas.removeEventListener('mouseout', handleMouseOut);
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDrawing, drawColor]);

  const handleUploadImage = async () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const dataURL = canvas.toDataURL('image/png');
      const response = await fetch(dataURL);
      const blob = await response.blob();
      const formData = new FormData();
      formData.append('image', blob, 'canvas-image.png');
      await axios.post('http://localhost:5000/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      alert('Image uploaded successfully!');
    }
  };

  const handleConvertToText = () => {
    setLoading(true);
    const canvas = canvasRef.current;
    const imageDataURL = canvas.toDataURL();

    fetch('https://api.ocr.space/parse/image', {
      method: 'POST',
      body: JSON.stringify({ base64Image: imageDataURL }),
      headers: {
        'Content-Type': 'application/json',
        'apikey': 'K81755459088957', // Replace with your OCR API key
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.IsErroredOnProcessing) {
          setError(data.ErrorMessage);
          setConvertedText('');
        } else {
          setConvertedText(data.ParsedResults[0]?.ParsedText || 'No text found');
          setError('');
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error:', error);
        setError('An error occurred while processing the image.');
        setConvertedText('');
        setLoading(false);
      });
  };

  const handleSaveText = async () => {
    try {
      await axios.post('http://localhost:5000/save-text', { text: convertedText });
      alert('Text extracted and saved successfully!');
    } catch (error) {
      console.error('Error saving text:', error);
      alert('Failed to save text.');
    }
  };

  const dataURItoBlob = (dataURI) => {
    const byteString = atob(dataURI.split(',')[1]);
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mimeString });
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, width, height);
  };

  return (
    <div style={{ marginTop: '50px', position: 'relative', backgroundColor: '#000000', opacity: 1 }}>
      <div className="mt-4 flex flex-wrap gap-2 justify-center" style={{ backgroundColor: '#000000', opacity: 1 }}>
        {Array.from({ length: 9 }, (_, i) => String.fromCharCode(49 + i)).map((char, index) => (
          <div key={index} className="flex flex-row">
            <button 
              onClick={() => changeMode(char)} 
              className="p-4 bg-indigo-600 text-white text-xl rounded hover:bg-indigo-500 transition flex justify-center"
            >
              {char}
            </button>
          </div>
        ))}
      </div>
      <div className='flex flex-row' style={{ opacity: 1 }}>
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          style={{
            border: '1px solid #ccc',
            backgroundColor: 'white',
            display: 'block',
            margin: '0 auto',
          }}
          className="canvas"
        />
      </div>
      <div className='flex flex-row'>
        <button onClick={clearCanvas}>Clear</button>
        <button onClick={onClose}>Close</button>
      </div>
    </div>
  );
};

export default NurseryLinesStencilsCanvas;
